import * as React from 'react';
import { cn } from '@/utils/cn';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  variant?: 'dark' | 'light';
}

/**
 * Input Component
 *
 * Variants:
 * - dark: Dark background, white text (default)
 * - light: White background, brand-800 text (for forms)
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, variant = 'dark', ...props }, ref) => {
    const isLight = variant === 'light';

    return (
      <input
        type={type}
        className={cn(
          'flex w-full rounded-md px-3 py-2 transition-all',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-300 focus-visible:ring-offset-0',
          'disabled:cursor-not-allowed disabled:opacity-50',
          !isLight && [
            'h-10 border border-white/20',
            'bg-brand-900/50 text-sm text-white',
            'placeholder:text-gray-400',
            'ring-offset-background',
          ],
          isLight && [
            'h-[40px]',
            'border border-gray-290',
            'bg-white',
            'font-sans text-14 font-semibold leading-160 tracking-neg-2',
            'text-brand-800',
            'placeholder:font-normal placeholder:text-gray-400',
          ],
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Input.displayName = 'Input';

export { Input };
